import React, { useEffect, useState } from 'react'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Icon from 'components_new/atoms/Icon'
import MenuItem from 'components_new/atoms/Menu/MenuItem'
import TextField from 'components_new/atoms/TextField'

import SidebarDialog from 'components_new/molecules/SidebarDialog'
import TabSwitch from 'components_new/molecules/TabSwitch'

import Checklist from './Checklist'
import DashboardView from './DashboardView'
import Metrics from './Metrics'
import UsabilityView from './UsabilityView'
import UsersView from './UsersView'

import MetricsLoading from './Metrics/loading'
import DashboardViewLoading from './DashboardView/loading'
import UsersViewLoading from './UsersView/loading'

import { TDashboardActivityPeriod } from './utils'
import {
  TDashboardGroup,
  TDashboardGroupActivity
} from 'types/GlobalDashboardGroup'
import { getActivities } from 'redux/api/DashboardGroups'
import { useTranslation } from 'language/translator'

interface ActivityDialogProps {
  dashboardGroup: TDashboardGroup | null
  onClose: () => void
  open: boolean
}

const ActivityDialog = (props: ActivityDialogProps) => {
  const { dashboardGroup, onClose, open } = props

  const [data, setData] = useState<TDashboardGroupActivity | null>(null)
  const [tab, setTab] = useState<'views' | 'users'>('views')
  const [showUsability, setShowUsability] = useState<boolean>(false)
  const [period, setPeriod] = useState<TDashboardActivityPeriod>('SINCE_START')
  const { t, translateEnum } = useTranslation()

  useEffect(() => {
    if (open && dashboardGroup) {
      getActivities(dashboardGroup.id)
        .then((response) => setData(response.data.data))
        .catch(() => {})
    }
  }, [open, dashboardGroup])

  function handleClose() {
    onClose()

    setData(null)
    setTab('views')
    setShowUsability(false)
    setPeriod('SINCE_START')
  }

  if (!dashboardGroup) return null

  return (
    <SidebarDialog
      actions={
        <Button variant="text" onClick={handleClose}>
          {t('global', 'close')}
        </Button>
      }
      fullHeight={true}
      fullWidth={true}
      leftSidebar={{
        content: (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {data ? (
              <Metrics
                dashboardGroupId={dashboardGroup.id}
                data={data}
                period={period}
              />
            ) : (
              <MetricsLoading />
            )}
          </Box>
        ),
        size: 'small'
      }}
      maxWidth={false}
      rightSidebar={
        showUsability
          ? undefined
          : {
              content: (
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                  }}
                >
                  <Checklist
                    dashboardGroupId={dashboardGroup.id}
                    onShowMore={() => setShowUsability(true)}
                  />
                </Box>
              )
            }
      }
      noGutter={true}
      onClose={handleClose}
      open={open}
      title={t('activity_overview', 'activity_overview')}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'background.paper',
          p: 2
        }}
      >
        {showUsability ? (
          <Button
            onClick={() => setShowUsability(false)}
            startIcon={<Icon name="ArrowBackOutlined" />}
            variant="text"
          >
            {t('global', 'back')}
          </Button>
        ) : (
          <>
            <TabSwitch
              onChange={(value) => setTab(value)}
              options={[
                { label: t('global', 'views'), value: 'views' },
                { label: t('global', 'users'), value: 'users' }
              ]}
              value={tab}
            />
            <TextField
              label={t('date_filters', 'time_period')}
              onChange={(event) =>
                setPeriod(event.target.value as TDashboardActivityPeriod)
              }
              select={true}
              size="small"
              sx={{ width: 200 }}
              value={period}
            >
              <MenuItem value="THIS_WEEK">
                {translateEnum('dashboard_activity_periods', 'THIS_WEEK').title}
              </MenuItem>
              <MenuItem value="THIS_MONTH">
                {
                  translateEnum('dashboard_activity_periods', 'THIS_MONTH')
                    .title
                }
              </MenuItem>
              <MenuItem value="THIS_YEAR">
                {translateEnum('dashboard_activity_periods', 'THIS_YEAR').title}
              </MenuItem>
              <MenuItem value="SINCE_START">
                {
                  translateEnum('dashboard_activity_periods', 'SINCE_START')
                    .title
                }
              </MenuItem>
            </TextField>
          </>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          gap: 2,
          p: 2,
          pt: 0
        }}
      >
        {showUsability ? (
          <UsabilityView dashboardGroupId={dashboardGroup.id} />
        ) : (
          <>
            {tab === 'views' ? (
              <>
                {data ? (
                  <DashboardView data={data} period={period} />
                ) : (
                  <DashboardViewLoading />
                )}
              </>
            ) : (
              <>
                {data ? (
                  <UsersView data={data} period={period} />
                ) : (
                  <UsersViewLoading />
                )}
              </>
            )}
          </>
        )}
      </Box>
    </SidebarDialog>
  )
}

export default ActivityDialog
